import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import styles from "./AppsPage.module.scss";
import { Eye, PencilSimple, Trash } from "@phosphor-icons/react";
import DataTable from "../components/DataTable/DataTable";

interface App {
  id: string;
  name: string;
  description?: string;
  isActive?: boolean;
  organizationId?: string;
  createdBy?: string;
  createdAt?: string;
}

const APPS_PER_PAGE = 10;

export default function AppsPage() {
  const [allApps, setAllApps] = useState<App[]>([]);
  const [filteredApps, setFilteredApps] = useState<App[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmDeleteApp, setConfirmDeleteApp] = useState<App | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const navigate = useNavigate();

  const fetchApps = async () => {
    try {
      const res = await api.get("/core/app/list");
      console.log('API response data:', res.data);
      let appsData: App[] = [];
      if (Array.isArray(res.data)) {
        appsData = res.data;
      } else if (res.data && Array.isArray(res.data.apps)) {
        appsData = res.data.apps;
      } else if (res.data && Array.isArray(res.data.data)) {
        appsData = res.data.data;
      } else {
        console.log('Unexpected response format:', res.data);
        appsData = [];
      }
      setAllApps(appsData);
      setFilteredApps(appsData);
    } catch (err) {
      console.error("Failed to load apps:", err);
      setAllApps([]);
      setFilteredApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  useEffect(() => {
    console.log('allApps updated:', allApps);
  }, [allApps]);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = allApps.filter((app) => app.name?.toLowerCase().includes(query));
    setFilteredApps(filtered);
    setCurrentPage(1);
  }, [searchQuery, allApps]);

  const handleDeleteApp = (app: App) => {
    setConfirmDeleteApp(app);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteApp) return;
    try {
      await api.post("/core/app/delete", { appId: confirmDeleteApp.id });
      fetchApps();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setShowDeleteModal(false);
      setConfirmDeleteApp(null);
    }
  };

  const columns = [
    {
      label: "Name",
      accessor: "name",
      render: (row: App) => row.name || "N/A",
    },
    {
      label: "Description",
      accessor: "description",
      render: (row: App) => row.description || "N/A",
    },
    {
      label: "Active",
      accessor: "isActive",
      render: (row: App) => 
        row.isActive === undefined 
          ? "N/A" 
          : row.isActive 
            ? "Yes" 
            : "No",
    },
    {
      label: "Organization ID",
      accessor: "organizationId",
      render: (row: App) => row.organizationId || "N/A",
    },
    {
      label: "Created By",
      accessor: "createdBy",
      render: (row: App) => row.createdBy || "N/A",
    },
    {
      label: "Created At",
      accessor: "createdAt",
      render: (row: App) => row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A",
    },
  ];

  const actions = (app: App) => (
    <div className={styles.actions}>
      <button
        onClick={() => navigate(`/apps/view/${app.id}`)}
        title="View"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', color: '#007bff' }}
      >
        <Eye size={20} />
      </button>
      <button
        onClick={() => navigate(`/apps/edit/${app.id}`)}
        title="Edit"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', color: '#007bff' }}
      >
        <PencilSimple size={20} />
      </button>
      <button
        onClick={() => handleDeleteApp(app)}
        title="Delete"
        className={styles.deleteBtn}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', color: '#dc3545' }}
      >
        <Trash size={20} />
      </button>
    </div>
  );

  const paginatedApps = filteredApps.slice(
    (currentPage - 1) * APPS_PER_PAGE,
    currentPage * APPS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredApps.length / APPS_PER_PAGE);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button 
          onClick={() => navigate("/apps/new")}
          style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '10px 20px', cursor: 'pointer', borderRadius: '4px' }}
        >
          + Add App
        </button>
        <input
          type="text"
          placeholder="Search apps..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {loading && <div>Loading...</div>}

      <DataTable
        columns={columns}
        data={paginatedApps}
        actions={actions}
        emptyMessage="No apps found."
      />

      {totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
            <button
              key={num}
              onClick={() => setCurrentPage(num)}
              className={num === currentPage ? styles.activePage : ""}
            >
              {num}
            </button>
          ))}
        </div>
      )}

      {showDeleteModal && confirmDeleteApp && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <p>
              Are you sure you want to delete{" "}
              <strong>{confirmDeleteApp.name}</strong>?
            </p>
            <div className={styles.modalActions}>
              <button onClick={confirmDelete} className={styles.confirmBtn}>
                Yes, Delete
              </button>
              <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}